import React, { useState, useEffect, useRef, useContext } from 'react'
import { sendTeaCommEvent, setStayPageBizParams } from 'lib/tea-analyze'
import Content from './components/content'
import RelatedSeries from './components/related-series'
import RelatedSeriesVertical from './components/related-series/vertical'
import Favour from './components/favour'
import FavourVertical from './components/favour/vertical'
import {
  ReportButton,
  InteractVideo,
} from 'Components/new/community-card/interact'
import Share from 'Components/new/community-card/share'
import RelatedRecommendVertical from './components/related-recommend/vertical'
import RecommendFeed from './components/recommend-feed'
import ImageStyles from 'Components/view-image/style'
import { GlobalStoreContext } from 'Components/store'
import { useResponsive } from 'Components/hooks/use-responsive'
import { useResponsiveContainer } from 'Components/hooks/use-responsive-container'
import ARTICLE_CONTENT_TYPE from 'Constants/articleType'
import Comment from 'Components/comment-3.0/comment'
import { ICommentData } from 'Components/comment-3.0/service'
import Head from 'next/head'

import StickyBottom from './components/sticky-bottom'
import {
  IArticle,
  IRelatedSeriesItem,
  IRelatedRecommendItem,
  IRecommendSeriesItem,
  IRelatedRecommendData,
} from './services/index'
import { scrollToWithStickyObserve } from 'Components/hooks/use-scroll-sticky-observe'
import HotContent from 'Components/new/feed/hot-content'
import { IStaticLabel } from 'Services/feed/static-labels'
import Copyright from 'Components/new/copyright'
import Statement from 'Components/footer-statement'
import Header from 'Components/new/community-card/header'
import classNames from 'classnames'
import useToggleDigg from 'Components/hooks/use-toggle-digg'
import NextPage from '../article-detail/components/next-page'
import useToggleCollect from 'Components/hooks/use-toggle-collect'

interface IProps {
  article: IArticle
  isVideo: boolean
  subArticleType: number
  entryFeed?: boolean
  extraTeaParams?: {
    channel_id: string
    channel_name: string
    tab_name: string
  }
  relatedRecommendData?: IRelatedRecommendData
  relatedSeriesData?: IRelatedSeriesItem[]
  recommendSeriesData?: IRecommendSeriesItem[]
  commentData?: ICommentData
  staticLabels?: IStaticLabel[]
  fontStyleString?: string
  handleType?: string
}

function usePrevious(value: any) {
  const ref = useRef()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

function Article({
  isVideo,
  article,
  subArticleType,
  entryFeed = false,
  extraTeaParams,
  relatedRecommendData,
  relatedSeriesData,
  recommendSeriesData,
  commentData,
  staticLabels = [],
  fontStyleString = '',
  handleType = 'off',
}: IProps) {
  const [columns, setColumns] = useState<number>(3)
  const { state: globalStore, dispatch } = useContext(GlobalStoreContext)
  const [relatedRecommendList, setRelatedRecommendList] = useState<
    IRelatedRecommendItem[]
  >(relatedRecommendData?.relation_list || [])
  const [nextItem, setNextItem] = useState<any>()
  const responsive = useResponsive()
  const cityName = globalStore.cityName || ''
  const gid = article?.gid
  const preGid = usePrevious(gid)
  const contentRef = useRef<HTMLDivElement | null>(null)
  const asideRef = useRef<HTMLDivElement | null>(null)
  const containerRef = useResponsiveContainer((obj: any) => {
    if (entryFeed) {
      if (obj.width < 974) {
        setColumns(3)
      } else {
        setColumns(4)
      }
    }
  })
  const contentTypeObj = ARTICLE_CONTENT_TYPE[subArticleType] || {}

  const extraTea = {
    page_id: entryFeed ? 'page_feed_article_mix' : 'page_pgc_article_detail',
    pre_page_id:
      typeof sessionStorage !== 'undefined'
        ? sessionStorage.getItem('pre_page_id') || ''
        : '',
    content_type: contentTypeObj?.value,
    ...(extraTeaParams || {}),
  }

  const allProps = {}

  if (!entryFeed && gid) {
    Object.assign(allProps, {
      'data-log-view-stay': JSON.stringify({
        event: 'stay_page',
        params: {},
      }),
    })
  }

  const onCommentShow = () => {
    sendTeaCommEvent('show_event', {
      event_id: 'comment_module',
      module_id: 'comment_module',
      group_id: gid,
      author_id: `${article?.media_user?.id}`,
      ...extraTea,
    })
  }

  const onReplyFold = (fold: boolean) => {
    sendTeaCommEvent('clk_event', {
      event_id: 'comment_module_shrink_comment',
      module_id: 'comment_module',
      group_id: gid,
      author_id: article?.media_user?.id,
      shrink_type: fold ? 'fold' : 'unfold',
      ...extraTea,
    })
  }

  const onClickCommentUser = (userId: string | number) => {
    sendTeaCommEvent('clk_event', {
      event_id: 'comment_module_user_portrait',
      module_id: 'comment_module',
      group_id: gid,
      author_id: article?.media_user?.id,
      to_user_id: String(userId),
      ...extraTea,
    })
  }

  const onClickLoadMoreComment = () => {
    sendTeaCommEvent('clk_event', {
      event_id: 'comment_module_load_more_comment',
      module_id: 'comment_module',
      group_id: gid,
      author_id: `${article?.media_user?.id}`,
      ...extraTea,
    })
  }

  const { update: collectUpdate, curStatus: collect_curStatus } =
    useToggleCollect({
      id: article?.gid,
      collected: article?.user_action?.is_collect,
    })

  const {
    update: diggUpdate,
    curStatus: digg_curStatus,
    curCount: digg_curCount,
  } = useToggleDigg({
    id: article?.gid,
    hasDigg: article?.user_action?.is_digg,
    diggCount: article?.digg_count,
    articleType: article?.article_type,
  })
  useEffect(() => {
    const next_item = relatedRecommendList[0]
    setNextItem(next_item as any)
  }, [relatedRecommendList])

  useEffect(() => {
    if (!entryFeed && preGid !== gid) {
      setStayPageBizParams({
        ...extraTea,
        group_id: gid,
        author_id: `${article?.media_user?.id}`,
      })

      sendTeaCommEvent('page_enter', {
        ...extraTea,
        group_id: gid,
        author_id: `${article?.media_user?.id}`,
      })
      sendTeaCommEvent('go_detail', {
        ...extraTea,
        group_id: gid,
        author_id: `${article?.media_user?.id}`,
      })
    }
  }, [gid, extraTea, article?.media_user?.id])
  useEffect(() => {
    if (!entryFeed) {
      window.onbeforeunload = function () {
        try {
          window.scrollTo(0, 0)
        } catch (e) {
          //兼容性降级，不需要监控
        }
      }
      return () => {
        window.onbeforeunload = null
      }
    }
  })

  useEffect(() => {
    if (entryFeed) {
      return
    }
    if (responsive?.['md']) {
      setColumns(4)
    } else {
      setColumns(3)
    }
  }, [responsive?.['md']])

  return (
    <div
      data-log-extra={JSON.stringify({
        ...extraTea,
        group_id: gid,
        author_id: `${article?.media_user?.id}`,
      })}
      {...allProps}
      className={`article clearfix ${entryFeed ? 'article-entry-feed' : ''}`}
    >
      <Head>
        <style>{fontStyleString}</style>
      </Head>
      <div className="tw-grid tw-grid-cols-12 tw-gap-x-12">
        <main
          className={`${
            entryFeed ? 'tw-col-span-12' : 'tw-col-span-9'
          } tw-flex tw-justify-center`}
        >
          <section
            className="tw-w-644 md:tw-w-680  xl:tw-w-800  "
            ref={containerRef}
          >
            <div className="content" ref={contentRef}>
              <Content
                digg_count={digg_curCount}
                collect_curStatus={collect_curStatus}
                digg_curStatus={digg_curStatus}
                collectUpdate={collectUpdate}
                diggUpdate={diggUpdate}
                article={article}
                isVideo={isVideo}
                group_id={gid}
              />
            </div>
            <div style={{ marginTop: 60 }}>
              <div>
                <RelatedSeries
                  groupId={gid}
                  isVideo={isVideo}
                  cityName={cityName}
                  data={relatedSeriesData}
                />
              </div>

              {!entryFeed && (
                <StickyBottom className="tw-h-22 tw-z-50">
                  {(isFixed) => (
                    <div
                      className={classNames(
                        'tw-grid tw-h-70 tw-w-full tw-grid-cols-12',
                        {
                          'tw-block': isFixed,
                          'tw-hidden': !isFixed,
                        }
                      )}
                    >
                      <div
                        className={classNames({
                          'tw-flex tw-items-center shadow tw-bg-white tw-pl-12 tw-pr-20 tw-h-full tw-col-span-9':
                            isFixed,
                        })}
                      >
                        <Header
                          userId={article?.media_user?.id}
                          username={`${article?.media_user?.screen_name}
                        `}
                          nameAppend={`${
                            article?.media_user?.auth_info?.auth_info
                              ? `· ${article?.media_user?.auth_info?.auth_info}`
                              : ''
                          }`}
                          avatar={article?.media_user?.avatar_url}
                          className="tw-flex-1 tw-overflow-hidden"
                          nameClassName="tw-text-video-shallow-gray tw-w-200 md:tw-w-450 lg:tw-w-750"
                          followVisible={false}
                          userVerified={
                            !!article?.media_user?.auth_info?.auth_type
                          }
                          verifyType={article?.media_user?.auth_info?.auth_type}
                        />
                        <InteractVideo
                          digg_count={digg_curCount}
                          comment_count={article?.comment_count}
                          is_collected={collect_curStatus}
                          is_digg={digg_curStatus}
                          moreContent={
                            <div className="tw-px-24 tw-py-12">
                              <ReportButton
                                objectType={5}
                                objectId={article?.gid}
                                authorId={article?.media_user?.id}
                                articleType={article?.article_type}
                                onClick={() => {
                                  sendTeaCommEvent('rt_report', {
                                    group_id: article?.gid,
                                    author_id: String(article?.media_user?.id),
                                    hudong_position: '0',
                                    content_type:
                                      ARTICLE_CONTENT_TYPE[
                                        article?.article_type!
                                      ]?.value,
                                    operation_type: '0',
                                  })
                                }}
                                onCancel={() => {
                                  sendTeaCommEvent('rt_report', {
                                    group_id: article?.gid,
                                    author_id: String(article?.media_user?.id),
                                    hudong_position: '0',
                                    content_type:
                                      ARTICLE_CONTENT_TYPE[
                                        article?.article_type!
                                      ]?.value,
                                    operation_type: '0',
                                  })
                                }}
                              />
                            </div>
                          }
                          shareContent={({ close }) => (
                            <Share
                              url={`https://www.dongchedi.com/article/${article?.gid}?zt=pc_redirect`}
                              title={article?.title}
                              onCopySuccess={close}
                              onShare={(text) => {
                                sendTeaCommEvent('clk_event', {
                                  event_id: 'ugc_fenxiang',
                                  author_id: String(article?.media_user?.id),
                                  hudong_position: '0',
                                  group_id: article?.gid,
                                  obj_text: text,
                                })
                              }}
                            />
                          )}
                          onClickComment={() => {
                            if (isFixed && contentRef.current) {
                              const { height } =
                                contentRef.current.getBoundingClientRect()
                              scrollToWithStickyObserve(height)
                            }
                            sendTeaCommEvent('rt_reply_comment', {
                              author_id: String(article?.media_user?.id),
                              hudong_position: '0',
                              group_id: article?.gid,
                              content_type:
                                ARTICLE_CONTENT_TYPE[article?.article_type!]
                                  ?.value,
                            })
                          }}
                          onClickDigg={(curStatus) => {
                            diggUpdate()
                            sendTeaCommEvent(
                              curStatus ? 'rt_unlike' : 'rt_like',
                              {
                                group_id: article?.gid,
                                author_id: String(article?.media_user?.id),
                                hudong_position: '0',
                                content_type:
                                  ARTICLE_CONTENT_TYPE[article?.article_type!]
                                    ?.value,
                              }
                            )
                          }}
                          onClickCollect={(curStatus) => {
                            collectUpdate()

                            sendTeaCommEvent(
                              curStatus ? 'rt_not_favourite' : 'rt_favourite',
                              {
                                group_id: article?.gid,
                                author_id: String(article?.media_user?.id),
                                hudong_position: '0',
                                content_type:
                                  ARTICLE_CONTENT_TYPE[article?.article_type!]
                                    ?.value,
                              }
                            )
                          }}
                        />
                      </div>
                      {nextItem && (
                        <div
                          className="shadow tw-bg-white tw-h-full tw-col-span-3 tw-ml-6"
                          data-log-view-config={JSON.stringify({
                            repeated: true,
                          })}
                          data-log-view={JSON.stringify({
                            event: 'show_event',
                            params: {
                              event_id: 'next_article_button',
                              button_name: '下一篇',
                              group_id: gid,
                              content_type:
                                ARTICLE_CONTENT_TYPE[article?.article_type]
                                  .value,
                              author_id: article?.media_user?.id,
                            },
                          })}
                          onClick={() => {
                            sendTeaCommEvent('clk_event', {
                              event_id: 'next_article_button',
                              button_name: '下一篇',
                              group_id: nextItem?.group_id_str,
                              content_type:
                                ARTICLE_CONTENT_TYPE[nextItem?.article_type]
                                  .value,
                              author_id: String(nextItem?.creator_uid),
                            })
                          }}
                        >
                          <NextPage nextItem={nextItem} />
                        </div>
                      )}
                    </div>
                  )}
                </StickyBottom>
              )}
              <div className="comment">
                {isVideo ||
                article?.content ||
                article?.gallery_info?.image_list?.length ? (
                  <Comment
                    gid={gid}
                    onShow={onCommentShow}
                    onReplyFold={onReplyFold}
                    onClickCommentUser={onClickCommentUser}
                    onClickLoadMoreComment={onClickLoadMoreComment}
                    initData={commentData}
                    author_id={article?.media_user?.id}
                    article_sub_type={article?.sub_article_type}
                  />
                ) : null}
              </div>

              {!entryFeed && <RecommendFeed columns={columns} />}
              <Statement />
            </div>
          </section>
        </main>
        {!entryFeed && (
          <aside className={`tw-col-span-3`} ref={asideRef}>
            <RelatedRecommendVertical
              title="相关推荐"
              groupId={gid}
              videoList={relatedRecommendList as any}
              setVideoList={setRelatedRecommendList}
              nextOffset={relatedRecommendData?.next_offset}
              isRecommond={relatedRecommendData?.is_recommend}
            />

            <HotContent
              data={staticLabels}
              renderLink={(item) => (content) =>
                (
                  <a href={`/news/${item.path}`} target={'_blank'}>
                    {content}
                  </a>
                )}
              onClick={(text) => {
                sendTeaCommEvent('clk_event', {
                  event_id: 'key_word',
                  obj_text: text || '',
                })
              }}
              onClickMore={() => {
                sendTeaCommEvent('clk_event', {
                  page_id: 'page_pgc_article_detail',
                  event_id: 'key_word',
                  obj_text: '全部资讯',
                })
              }}
            />
            <Copyright />
          </aside>
        )}
      </div>
      <style jsx global>
        {ImageStyles}
      </style>
      <style jsx>
        {`
          .article {
            padding-bottom: 60px;
          }
          .content {
            width: 100%;
            max-width: 900px;
            margin: 0 auto;
          }
          @media (max-width: 1060px) {
            section {
              ${handleType === 'off' ? '' : 'width: 556px'};
            }
          }
        `}
      </style>
    </div>
  )
}

export default Article
